import { buttonUrl, isButton } from '@bw/apis/contentful';
import { cn } from '@bw/core';
import { Image, RemoteSvg } from '@bw/elements';
import Link from 'next/link';
import { Fragment } from 'react/jsx-runtime';
import { Block } from '../../factories/types';
import { annotate } from '../../utils/annotate';
import { assetUri } from '../../utils/assetUri';
import { defined } from '../../utils/defined';
import { RichText } from '../../utils/rich-text/RichText';
import styles from './footer.module.scss';

export const Footer: Block<'footer'> = ({ data }) => {
	const { fields } = data;
	return (
		<footer className={cn(styles.footerContainer, 'emphasized')}>
			{fields.logo &&
				<figure className={styles.footerLogo} role="none">
					<Image
						src={assetUri(fields.logo)}
						alt={fields.logo.fields.title ?? ''}
						width={200}
						height={190}
					/>
				</figure>
			}

			<div className={styles.footerTextSection}>
				<div className={styles.footerSections}>
					{defined(fields.menus).map((menu, index) =>
						<div key={index} className={styles['footerSection']} {...annotate(menu)}>
							<span className={styles.listHeader}>{menu.fields.title}</span>
							<ul>
								{defined(menu.fields.menuItems).map((menuItem, subIndex) =>
									isButton(menuItem) &&
									<li key={subIndex}>
										<Link href={buttonUrl(menuItem)}>
											{menuItem.fields.title}
										</Link>
									</li>
								)}
							</ul>
						</div>
					)}
					<div className={styles.AddressIconContainer}>
						{fields.phoneNumber &&
							<div className={styles.addressSection}>
								<span className={styles.addressHeading}>Need help on an existing policy?</span>
								<p>
									<a
										href={`${fields.phoneNumberType}:+1${fields.phoneNumber.replace(/\D/g, '')}`}
										title={fields.phoneNumber}
									>
										{fields.phoneNumberType === 'tel' ? 'Call' : 'Text'}: {fields.phoneNumber}
									</a>
								</p>
							</div>
						}
						<div className={styles.addressSection}>
							<p {...annotate(data, 'address')}>
								{fields.address?.split('\n').map((line, index) =>
									<Fragment key={index}>
										{line}
										<br />
									</Fragment>
								)}
							</p>
						</div>
						<div className={styles.socialIcons} {...annotate(data, 'socialIcons')}>
							{defined(fields.socialIcons).map((icon, index) =>
								icon.fields.button?.fields.url &&
								<Link
									key={index}
									href={icon.fields.button?.fields.url}
									target="_blank"
									title={icon.fields.button.fields.title ?? ''}
									aria-label={`${icon.fields.button.fields.title} (opens in a new tab)`}
								>
									{icon.fields.image 
										? <RemoteSvg url={assetUri(icon.fields.image)} width={24} height={24} />
										: icon.fields.button.fields.title
									}
								</Link>
							)}
						</div>
					</div>
				</div>

				<div className={styles.footerBottom}>
					<div className={styles.linksCopyrightWrapper}>
						<div className={styles.footerCopyright}>
							<RichText document={fields.copyright} />
						</div>

						<ul className={styles.footerLinks}>
							{defined(fields.legalNoticeLinks).map((item, index) =>
								item.fields.url &&
								<li key={index}>
									<Link href={item.fields.url}>{item.fields.title}</Link>
								</li>
							)}
						</ul>
					</div>
					{fields.legalNoticeSubText &&
						<div className={cn(styles.footerCopyright, styles.captchaNotice)}>
							<RichText document={fields.legalNoticeSubText} />
						</div>
					}
				</div>
			</div>
		</footer>
	);
};
